import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Ticket,
  Tag,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Percent,
  DollarSign,
  Users,
  FormInput,
  ToggleLeft,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Gift,
  Send,
  Mail,
  Copy,
  Building2,
  MapPin,
  Tent,
  Music,
  Mic,
  Coffee,
  Theater,
  Sparkles,
  CreditCard,
  Eye,
  EyeOff,
} from "lucide-react";

const ROOM_ICONS_ADMIN = { Building2, MapPin, Tent, Music, Mic, Coffee, Theater, Sparkles, Calendar };
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { InstallmentConfigSection } from "./admin/InstallmentConfigSection";
import { ParentalAuthConfigSection } from "./admin/ParentalAuthConfigSection";

import { API_URL } from "../config";

// ==================== SESSION MANAGEMENT ====================

export const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showRooms, setShowRooms] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: "", description: "", icon: "Building2", show_public: true, show_session_count: true });
  const [editingRoom, setEditingRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [newSession, setNewSession] = useState({
    date: "",
    time: "",
    total_seats: 100,
    name: "",
    subtitle: "",
    show_name: true,
    show_subtitle: true,
    show_date: true,
    show_time: true,
    show_total_seats: true,
    room_id: null,
  });
  const [expandedSession, setExpandedSession] = useState(null);
  const [reconcilingSessions, setReconcilingSessions] = useState(false);
  // Clone-session dialog state
  const [cloneSessionSrc, setCloneSessionSrc] = useState(null);
  const [cloneSessionPayload, setCloneSessionPayload] = useState(null);
  const [cloningSession, setCloningSession] = useState(false);

  // Bulk installments dialog (applies Pagamento Faseado settings across ALL ticket types)
  const [bulkInstOpen, setBulkInstOpen] = useState(false);
  const [bulkInstValue, setBulkInstValue] = useState({
    installments_enabled: true,
    min_first_payment_percent: 25,
    min_installment_amount: 10,
    installment_deadline_type: "before_session",
    installment_deadline_days: 7,
    seat_lock_policy: "from_first",
    suggested_plans: [2, 3, 4],
    cancellation_policy: "partial_refund",
    cancellation_fee_percent: 10,
    expired_policy: "auto_cancel",
    reminder_days_before: [7, 3, 1],
  });
  const [bulkInstFieldsToSend, setBulkInstFieldsToSend] = useState(new Set());
  const [bulkInstBusy, setBulkInstBusy] = useState(false);

  // Toggle a field in the "will-be-sent" set (checkbox next to each option)
  const toggleBulkField = (key, on) => {
    setBulkInstFieldsToSend((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  const applyBulkInstallments = async () => {
    if (bulkInstFieldsToSend.size === 0) {
      toast.error("Selecciona pelo menos um campo para aplicar");
      return;
    }
    const payload = {};
    bulkInstFieldsToSend.forEach((k) => { payload[k] = bulkInstValue[k]; });
    setBulkInstBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/ticket-types/bulk-installments`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Aplicado a ${r.data.modified} tipo(s) de bilhete (${r.data.matched} correspondência(s))`);
      setBulkInstOpen(false);
      setBulkInstFieldsToSend(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar configuração faseada");
    } finally { setBulkInstBusy(false); }
  };

  const openCloneSession = (session) => {
    setCloneSessionSrc(session);
    setCloneSessionPayload({
      name: ((session.name || "").trim() ? `${session.name} (cópia)` : "(cópia)"),
      subtitle: session.subtitle || "",
      date: session.date || "",
      time: session.time || "",
      total_seats: session.total_seats || 100,
      room_id: session.room_id || null,
      include_ticket_types: true,
    });
  };

  const submitCloneSession = async () => {
    if (!cloneSessionSrc) return;
    setCloningSession(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/sessions/${cloneSessionSrc.id}/clone`,
        cloneSessionPayload,
        { withCredentials: true }
      );
      toast.success(`Sessão duplicada (${r.data.cloned_ticket_types} bilhete(s) clonado(s))`);
      setCloneSessionSrc(null);
      setCloneSessionPayload(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao clonar sessão");
    } finally {
      setCloningSession(false);
    }
  };

  const handleReconcileSessions = async () => {
    if (!window.confirm("Reconciliar lugares disponíveis a partir dos bilhetes reais? Corrige eventuais discrepâncias.")) return;
    setReconcilingSessions(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/sessions/reconcile-seats`, {}, { withCredentials: true });
      toast.success(r.data.message);
      if (r.data.corrections?.length > 0) console.log("Sessions reconcile:", r.data.corrections);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reconciliar");
    } finally { setReconcilingSessions(false); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, typesRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sessions/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/ticket-types`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rooms`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSessions(sessionsRes.data);
      setTicketTypes(typesRes.data);
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createRoom = async () => {
    if (!newRoom.name.trim()) return toast.error("Nome da sala é obrigatório");
    try {
      await axios.post(`${API_URL}/api/admin/rooms`, newRoom, { withCredentials: true });
      toast.success("Sala criada");
      setNewRoom({ name: "", description: "", icon: "Building2", show_public: true, show_session_count: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar sala"); }
  };

  const saveRoomEdit = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/rooms/${editingRoom.id}`,
        { name: editingRoom.name, description: editingRoom.description, icon: editingRoom.icon || "Building2", show_public: editingRoom.show_public !== false, show_session_count: editingRoom.show_session_count !== false },
        { withCredentials: true }
      );
      toast.success("Sala atualizada");
      setEditingRoom(null);
      fetchData();
    } catch (e) { toast.error("Erro ao atualizar sala"); }
  };

  const assignSessionToRoom = async (sessionId, roomId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/session/${sessionId}`,
        { room_id: roomId },
        { withCredentials: true }
      );
      toast.success(roomId ? "Sessão atribuída à sala" : "Sessão removida da sala");
      fetchData();
    } catch (e) { toast.error("Erro ao mover sessão"); }
  };

  const moveSession = async (sessionId, direction) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/sessions/${sessionId}/move-${direction}`,
        {},
        { withCredentials: true }
      );
      fetchData();
    } catch (e) { toast.error("Erro ao reordenar"); }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm("Eliminar esta sala? As sessões associadas ficarão sem sala (não são apagadas).")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/rooms/${roomId}`, { withCredentials: true });
      toast.success("Sala eliminada");
      fetchData();
    } catch (e) { toast.error("Erro ao eliminar sala"); }
  };

  const handleCreateSession = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/sessions`, newSession, { withCredentials: true });
      toast.success("Sessão criada com sucesso!");
      setIsCreateOpen(false);
      setNewSession({
        date: "", time: "", total_seats: 100, name: "", subtitle: "",
        show_name: true, show_subtitle: true, show_date: true, show_time: true, show_total_seats: true,
        room_id: null,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar sessão");
    }
  };

  const handleUpdateSession = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/session/${editingSession.id}`,
        {
          date: editingSession.date,
          time: editingSession.time,
          total_seats: editingSession.total_seats,
          name: editingSession.name || "",
          subtitle: editingSession.subtitle || "",
          show_name: editingSession.show_name !== false,
          show_subtitle: editingSession.show_subtitle !== false,
          show_date: editingSession.show_date !== false,
          show_time: editingSession.show_time !== false,
          show_total_seats: editingSession.show_total_seats !== false,
          room_id: editingSession.room_id || null,
        },
        { withCredentials: true }
      );
      toast.success("Sessão atualizada!");
      setEditingSession(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar sessão");
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/sessions/${sessionId}`, { withCredentials: true });
      toast.success("Sessão eliminada!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar sessão");
    }
  };

  const toggleSessionActive = async (session) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/session/${session.id}`,
        { active: session.active === false ? true : false },
        { withCredentials: true }
      );
      toast.success(session.active !== false ? "Sessão ocultada" : "Sessão visível");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar sessão");
    }
  };

  const getSessionTicketTypes = (sessionId) => {
    return ticketTypes.filter((t) => t.session_id === sessionId);
  };

  if (loading) return <div className="animate-pulse">A carregar...</div>;

  return (
    <div className="space-y-6" data-testid="session-management">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Gestão de Salas, Sessões e Bilhetes</h3>
          <p className="text-zinc-500 text-sm">Salas são opcionais. Crie-as para agrupar sessões (multi-palco, multi-sala, breakout rooms).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowRooms((v) => !v)} variant="outline" className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10" data-testid="toggle-rooms-section">
            <Building2 className="w-4 h-4 mr-2" /> Salas ({rooms.length}) {showRooms ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
          <Button onClick={() => setBulkInstOpen(true)} variant="outline" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="bulk-installments-btn" title="Aplicar configurações de Pagamento Faseado a todos os tipos de bilhete de uma só vez">
            <CreditCard className="w-4 h-4 mr-2" /> Faseado a Todos
          </Button>
          <Button onClick={handleReconcileSessions} disabled={reconcilingSessions} variant="outline" className="border-white/10 text-zinc-300" data-testid="btn-reconcile-sessions" title="Recalcula os lugares disponíveis a partir dos bilhetes reais">
            {reconcilingSessions ? "A reconciliar..." : "Reconciliar"}
          </Button>
          <Button onClick={() => setIsCreateOpen(true)} className="btn-primary" data-testid="create-session-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Sessão
          </Button>
        </div>
      </div>

      {showRooms && (
        <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4 space-y-3" data-testid="rooms-section">
          <p className="text-violet-300 text-xs uppercase tracking-wider font-semibold">Salas (opcional)</p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Nome</Label>
              <Input
                value={newRoom.name}
                onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                placeholder="Ex: Sala Principal · Palco A"
                className="input-dark mt-1"
                data-testid="new-room-name"
              />
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Descrição curta</Label>
              <Input
                value={newRoom.description}
                onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                placeholder="Ex: 500 lugares · perto do palco"
                className="input-dark mt-1"
                data-testid="new-room-desc"
              />
            </div>
            <div className="w-36">
              <Label className="text-zinc-400 text-xs">Ícone</Label>
              <Select value={newRoom.icon || "Building2"} onValueChange={(v) => setNewRoom({ ...newRoom, icon: v })}>
                <SelectTrigger className="input-dark mt-1" data-testid="new-room-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {["Building2","MapPin","Tent","Music","Mic","Coffee","Theater","Sparkles","Calendar"].map((n) => (
                    <SelectItem key={n} value={n} className="text-white">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col items-center gap-1 pb-1.5">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
              <Switch
                checked={newRoom.show_public !== false}
                onCheckedChange={(c) => setNewRoom({ ...newRoom, show_public: c })}
                className="data-[state=checked]:bg-violet-500"
              />
            </div>
            <div className="flex flex-col items-center gap-1 pb-1.5" title="Mostrar contagem de sessões dentro desta sala">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider text-center leading-tight">Mostrar<br/>nº sessões</span>
              <Switch
                checked={newRoom.show_session_count !== false}
                onCheckedChange={(c) => setNewRoom({ ...newRoom, show_session_count: c })}
                className="data-[state=checked]:bg-violet-500"
                data-testid="new-room-toggle-session-count"
              />
            </div>
            <Button onClick={createRoom} className="btn-primary" data-testid="create-room-btn">
              <Plus className="w-4 h-4 mr-1" /> Adicionar Sala
            </Button>
          </div>
          {rooms.length > 0 && (
            <p className="text-zinc-500 text-[11px] italic flex items-center gap-2">
              <GripVertical className="w-3.5 h-3.5" /> Dica: arraste qualquer sessão (abaixo) para uma sala para atribuí-la rapidamente.
            </p>
          )}
          {rooms.length > 0 && (
            <div className="space-y-2">
              <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-zinc-500"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ring-2","ring-zinc-500")}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("ring-2","ring-zinc-500");
                  const sessionId = e.dataTransfer.getData("text/plain");
                  if (sessionId) assignSessionToRoom(sessionId, null);
                }}
                className="border border-dashed border-zinc-700 rounded-lg p-2 text-center text-zinc-500 text-xs transition-all flex items-center justify-center gap-2"
                data-testid="unassign-drop-zone"
                title="Arraste uma sessão para aqui para a remover da sala"
              >
                <X className="w-3.5 h-3.5" /> Largue aqui uma sessão para retirar a sala (ficará "Sem sala")
              </div>
              {rooms.map((r) => {
                const isEdit = editingRoom?.id === r.id;
                return (
                  <div
                    key={r.id}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-violet-400"); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("ring-2","ring-violet-400")}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("ring-2","ring-violet-400");
                      const sessionId = e.dataTransfer.getData("text/plain");
                      if (sessionId) assignSessionToRoom(sessionId, r.id);
                    }}
                    className="flex items-center gap-3 bg-zinc-900/50 border border-white/5 rounded-lg p-2 transition-all"
                    data-testid={`room-row-${r.id}`}
                    title="Arraste uma sessão para aqui para atribuir"
                  >
                    {isEdit ? (
                      <>
                        <Input
                          value={editingRoom.name}
                          onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                          className="input-dark flex-1"
                        />
                        <Input
                          value={editingRoom.description || ""}
                          onChange={(e) => setEditingRoom({ ...editingRoom, description: e.target.value })}
                          className="input-dark flex-1"
                          placeholder="Descrição"
                        />
                        <Select value={editingRoom.icon || "Building2"} onValueChange={(v) => setEditingRoom({ ...editingRoom, icon: v })}>
                          <SelectTrigger className="input-dark w-32"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            {["Building2","MapPin","Tent","Music","Mic","Coffee","Theater","Sparkles","Calendar"].map((n) => (
                              <SelectItem key={n} value={n} className="text-white">{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Switch
                          checked={editingRoom.show_public !== false}
                          onCheckedChange={(c) => setEditingRoom({ ...editingRoom, show_public: c })}
                          className="data-[state=checked]:bg-violet-500"
                          title="Mostrar no público"
                        />
                        <Switch
                          checked={editingRoom.show_session_count !== false}
                          onCheckedChange={(c) => setEditingRoom({ ...editingRoom, show_session_count: c })}
                          className="data-[state=checked]:bg-amber-500"
                          title="Mostrar contagem de sessões no público"
                        />
                        <Button size="sm" onClick={saveRoomEdit} className="btn-primary">Guardar</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRoom(null)} className="text-zinc-400">×</Button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 flex items-center gap-2">
                          {(() => {
                            const IconC = ROOM_ICONS_ADMIN[r.icon] || Building2;
                            return <IconC className="w-4 h-4 text-violet-300" />;
                          })()}
                          <div>
                            <p className="text-white text-sm font-medium">{r.name}{!r.show_public && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300">Oculta no público</span>}</p>
                            {r.description && <p className="text-zinc-500 text-xs">{r.description}</p>}
                          </div>
                        </div>
                        <span className="text-zinc-500 text-xs">{sessions.filter((s) => s.room_id === r.id).length} sessões</span>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRoom(r)} className="text-amber-400"><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRoom(r.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="space-y-6">
        {(() => {
          // Group sessions by room (preserves backend ordering) and render
          // a clear header per room so the user understands that up/down
          // arrows reorder WITHIN each room independently.
          const groups = [];
          const seen = new Map();
          sessions.forEach((s) => {
            const key = s.room_id || "__no_room__";
            if (!seen.has(key)) {
              seen.set(key, groups.length);
              const r = s.room_id ? rooms.find((x) => x.id === s.room_id) : null;
              groups.push({ key, room: r, label: r ? r.name : "(sem sala)", items: [] });
            }
            groups[seen.get(key)].items.push(s);
          });
          return groups.map((g) => {
            const IconC = g.room ? (ROOM_ICONS_ADMIN[g.room.icon] || Building2) : Building2;
            return (
              <div key={g.key} className="space-y-3">
                <div className="flex items-center gap-2 px-1">
                  <IconC className="w-4 h-4 text-violet-300" />
                  <h4 className="text-violet-300 text-xs uppercase tracking-wider font-semibold">{g.label}</h4>
                  <span className="text-zinc-600 text-xs">· {g.items.length} sessão(ões)</span>
                </div>
                <div className="space-y-3">
                  {g.items.map((session) => (
                    <div
                      key={session.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", session.id); e.dataTransfer.effectAllowed = "move"; }}
                      className={`bg-zinc-900 rounded-xl border overflow-hidden ${session.active !== false ? "border-white/10" : "border-red-500/20 opacity-60"}`}
                    >
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
              onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
              data-testid={`session-row-${session.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {session.name && (
                      <p className="text-white font-semibold uppercase tracking-wide">{session.name}</p>
                    )}
                    {session.subtitle && (
                      <p className="text-amber-300 text-sm font-medium uppercase tracking-wider">{session.subtitle}</p>
                    )}
                    {!session.name && !session.subtitle && (
                      <p className="text-white font-medium">{session.date || "(sem data)"}</p>
                    )}
                    {session.room_id && (() => {
                      const r = rooms.find((x) => x.id === session.room_id);
                      if (!r) return null;
                      const IconC = ROOM_ICONS_ADMIN[r.icon] || Building2;
                      return (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1">
                          <IconC className="w-3 h-3" />
                          {r.name}
                        </span>
                      );
                    })()}
                    {(() => {
                      const ttCount = getSessionTicketTypes(session.id).length;
                      return (
                        <span className="text-zinc-500 text-xs">· {ttCount} {ttCount === 1 ? "tipo de bilhete" : "tipos de bilhete"}</span>
                      );
                    })()}
                  </div>
                  <p className="text-zinc-500 text-sm flex items-center gap-2 flex-wrap">
                    {session.date && (
                      <>
                        <Calendar className="w-4 h-4" />
                        <span>{session.date}</span>
                        <span className="text-zinc-600">·</span>
                      </>
                    )}
                    <Clock className="w-4 h-4" />
                    <span>{session.time || "—"}</span>
                    <span className="text-zinc-600">•</span>
                    <span className="text-emerald-400">{(session.sold_count ?? 0)} vendidos</span>
                    {(session.pending_count ?? 0) > 0 && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="text-amber-400">{session.pending_count} pendentes</span>
                      </>
                    )}
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-300">
                      {(session.available_actual ?? session.available_seats)} disponíveis
                    </span>
                    <span className="text-zinc-600">de {session.total_seats}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()} title="Reordenar">
                  <button
                    onClick={() => moveSession(session.id, "up")}
                    className="p-0.5 rounded hover:bg-white/10 text-zinc-400 hover:text-amber-400 transition"
                    data-testid={`move-up-session-${session.id}`}
                    aria-label="Mover para cima"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveSession(session.id, "down")}
                    className="p-0.5 rounded hover:bg-white/10 text-zinc-400 hover:text-amber-400 transition"
                    data-testid={`move-down-session-${session.id}`}
                    aria-label="Mover para baixo"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={session.active !== false}
                    onCheckedChange={() => toggleSessionActive(session)}
                    className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-zinc-600"
                    data-testid={`toggle-session-${session.id}`}
                  />
                  <span className={`text-xs font-medium ${session.active !== false ? "text-green-400" : "text-red-400"}`}>{session.active !== false ? "Visível" : "Oculta"}</span>
                </div>
                {expandedSession === session.id ? (
                  <ChevronUp className="w-5 h-5 text-zinc-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-500" />
                )}
              </div>
            </div>

            {expandedSession === session.id && (
              <div className="border-t border-white/10 p-4 space-y-4">
                {/* Edit Session — input + toggle "Mostrar no Público" inline */}
                {(() => {
                  const isEditing = editingSession?.id === session.id;
                  const cur = (k, def) => (isEditing ? (editingSession[k] !== undefined ? editingSession[k] : (session[k] !== undefined ? session[k] : def)) : (session[k] !== undefined ? session[k] : def));
                  const setF = (k, val) => setEditingSession({ ...session, ...editingSession, [k]: val });
                  return (
                    <>
                      {rooms.length > 0 && (
                        <div>
                          <Label className="text-zinc-400 text-sm">Sala <span className="text-zinc-600 text-xs">(opcional)</span></Label>
                          <Select
                            value={cur("room_id", null) || "__NONE__"}
                            onValueChange={(v) => setF("room_id", v === "__NONE__" ? null : v)}
                          >
                            <SelectTrigger className="input-dark mt-1" data-testid={`edit-session-room-${session.id}`}>
                              <SelectValue placeholder="Sem sala" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              <SelectItem value="__NONE__" className="text-zinc-400">— Sem sala —</SelectItem>
                              {rooms.map((r) => (
                                <SelectItem key={r.id} value={r.id} className="text-white">{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-zinc-400 text-sm">Nome da Sessão <span className="text-zinc-600 text-xs">(opcional)</span></Label>
                          <Input
                            value={cur("name", "")}
                            onChange={(e) => setF("name", e.target.value)}
                            placeholder="Ex: 1ª Sessão · Manhã"
                            className="input-dark mt-1"
                            data-testid={`edit-session-name-${session.id}`}
                          />
                        </div>
                        <div className="flex flex-col items-center gap-1 pb-1.5">
                          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                          <Switch
                            checked={cur("show_name", true) !== false}
                            onCheckedChange={(c) => setF("show_name", c)}
                            className="data-[state=checked]:bg-violet-500"
                            data-testid={`edit-session-toggle-show_name-${session.id}`}
                          />
                        </div>
                      </div>
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-zinc-400 text-sm">Subtítulo <span className="text-zinc-600 text-xs">(opcional)</span></Label>
                          <Input
                            value={cur("subtitle", "")}
                            onChange={(e) => setF("subtitle", e.target.value)}
                            placeholder="Ex: KID EXTERNO"
                            className="input-dark mt-1"
                            data-testid={`edit-session-subtitle-${session.id}`}
                          />
                        </div>
                        <div className="flex flex-col items-center gap-1 pb-1.5">
                          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                          <Switch
                            checked={cur("show_subtitle", true) !== false}
                            onCheckedChange={(c) => setF("show_subtitle", c)}
                            className="data-[state=checked]:bg-violet-500"
                            data-testid={`edit-session-toggle-show_subtitle-${session.id}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-zinc-400 text-sm">Data</Label>
                            <Input
                              type="date"
                              value={(cur("date", "") || "").slice(0, 10)}
                              onChange={(e) => setF("date", e.target.value)}
                              className="input-dark mt-1"
                              data-testid={`edit-session-date-${session.id}`}
                            />
                          </div>
                          <div className="flex flex-col items-center gap-1 pb-1.5">
                            <span className="text-[10px] text-zinc-500 uppercase">Público</span>
                            <Switch
                              checked={cur("show_date", true) !== false}
                              onCheckedChange={(c) => setF("show_date", c)}
                              className="data-[state=checked]:bg-violet-500"
                              data-testid={`edit-session-toggle-show_date-${session.id}`}
                            />
                          </div>
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-zinc-400 text-sm">Horário</Label>
                            <Input
                              type="time"
                              value={cur("time", "")}
                              onChange={(e) => setF("time", e.target.value)}
                              className="input-dark mt-1"
                              data-testid={`edit-session-time-${session.id}`}
                            />
                          </div>
                          <div className="flex flex-col items-center gap-1 pb-1.5">
                            <span className="text-[10px] text-zinc-500 uppercase">Público</span>
                            <Switch
                              checked={cur("show_time", true) !== false}
                              onCheckedChange={(c) => setF("show_time", c)}
                              className="data-[state=checked]:bg-violet-500"
                              data-testid={`edit-session-toggle-show_time-${session.id}`}
                            />
                          </div>
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-zinc-400 text-sm">Lugares Totais</Label>
                            <Input
                              type="number"
                              value={cur("total_seats", 0)}
                              onChange={(e) => setF("total_seats", parseInt(e.target.value))}
                              className="input-dark mt-1"
                            />
                          </div>
                          <div className="flex flex-col items-center gap-1 pb-1.5">
                            <span className="text-[10px] text-zinc-500 uppercase">Público</span>
                            <Switch
                              checked={cur("show_total_seats", true) !== false}
                              onCheckedChange={(c) => setF("show_total_seats", c)}
                              className="data-[state=checked]:bg-violet-500"
                              data-testid={`edit-session-toggle-show_total_seats-${session.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div className="flex gap-2">
                  {editingSession?.id === session.id && (
                    <Button onClick={handleUpdateSession} className="btn-primary" size="sm">
                      <Save className="w-4 h-4 mr-1" />
                      Guardar
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="text-cyan-300 border-cyan-400/30 hover:bg-cyan-500/10"
                    size="sm"
                    onClick={() => openCloneSession(session)}
                    data-testid={`clone-session-${session.id}`}
                    title="Duplicar esta sessão (e opcionalmente os tipos de bilhete)"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Duplicar Sessão
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-500/10" size="sm">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar Sessão
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-zinc-900 border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Eliminar Sessão</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          Tem a certeza? Esta ação eliminará também os tipos de bilhete associados.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteSession(session.id)}
                          className="bg-red-500 text-white hover:bg-red-600"
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                {/* Ticket Types for this session */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <TicketTypeList sessionId={session.id} onUpdate={fetchData} />
                </div>
              </div>
            )}
          </div>
                  ))}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Bulk Installments (applies "Pagamento Faseado" to all ticket types) */}
      <Dialog open={bulkInstOpen} onOpenChange={setBulkInstOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-installments-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" /> Pagamento Faseado — Aplicar a Todos
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Define aqui os valores de <strong>Pagamento Faseado</strong> e escolhe (com as caixas à esquerda de cada bloco) quais os campos que queres <strong>aplicar a todos os tipos de bilhete</strong> em simultâneo. Campos não seleccionados mantêm-se inalterados em cada bilhete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-200">
              <span>⚠️</span>
              <span>
                Esta acção afecta <strong>{ticketTypes.length}</strong> tipo(s) de bilhete de todas as sessões/salas e <strong>cascatea aos bilhetes já vendidos</strong>: as prestações NÃO pagas são redistribuídas proporcionalmente e os campos informativos (política de cancelamento, dias de lembrete, política de expirado) são copiados para cada registo. As prestações já pagas ficam intactas.
                <br /><br />
                <strong>Prazos limite</strong> (installment_deadline_type/days) só se aplicam a compras <strong>futuras</strong> — bilhetes existentes mantêm o prazo original via <em>deadline_override</em> para evitar cancelamentos automáticos indevidos.
              </span>
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {[
                ["installments_enabled", "Activar/Desactivar faseado"],
                ["min_first_payment_percent", "% mínima da 1ª prestação"],
                ["min_installment_amount", "Valor mínimo por prestação"],
                ["installment_deadline_type", "Tipo de prazo limite"],
                ["installment_deadline_days", "Nº dias antes da sessão"],
                ["seat_lock_policy", "Política de reserva de lugar"],
                ["suggested_plans", "Planos sugeridos (2, 3, 4…)"],
                ["cancellation_policy", "Política de cancelamento"],
                ["cancellation_fee_percent", "Taxa cancelamento (%)"],
                ["expired_policy", "Política de plano expirado"],
                ["reminder_days_before", "Dias de lembrete (ex: 7,3,1)"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer">
                  <Checkbox
                    checked={bulkInstFieldsToSend.has(key)}
                    onCheckedChange={(v) => toggleBulkField(key, !!v)}
                    data-testid={`bulk-inst-field-${key}`}
                  />
                  <span className="text-white text-xs">{label}</span>
                </label>
              ))}
            </div>

            <InstallmentConfigSection value={bulkInstValue} onChange={setBulkInstValue} defaultOpen />
          </div>
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs"
                onClick={async () => {
                  if (!window.confirm("Restaurar todos os planos cancelados automaticamente pelo sweeper nas últimas 24 horas?\n\nUse esta acção só se um Faseado a Todos causou cancelamentos indevidos.")) return;
                  try {
                    const r = await axios.post(`${API_URL}/api/admin/plans/restore-auto-cancelled`, { since_hours: 24 }, { withCredentials: true });
                    toast.success(`Restaurados ${r.data.restored} plano(s)`);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
                }}
                data-testid="restore-auto-cancelled-btn"
                title="Restaurar planos cancelados por engano nas últimas 24h"
              >
                Restaurar Cancelamentos
              </Button>
              <Button
                variant="outline"
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 text-xs"
                onClick={async () => {
                  const raw = window.prompt("Que percentagem de agravamento aplicar? (deixa vazio para usar o pct configurado no Show/Tipos)\n\nExemplo: 3 para aplicar 3%", "3");
                  if (raw === null) return;
                  const forced = raw.trim() === "" ? null : Number(raw);
                  const body = forced !== null && !Number.isNaN(forced) ? { dry_run: true, pct: forced } : { dry_run: true };
                  try {
                    const dry = await axios.post(`${API_URL}/api/admin/plans/apply-installment-surcharge`, body, { withCredentials: true });
                    const n = dry.data.would_update || 0;
                    if (n === 0) {
                      const s = dry.data.skips || {};
                      const reasons = [];
                      if (s.pct_zero) reasons.push(`${s.pct_zero} com pct=0 (Show/Tipo sem % configurada)`);
                      if (s.already_applied) reasons.push(`${s.already_applied} já com surcharge aplicado`);
                      if (s.final_higher) reasons.push(`${s.final_higher} com final_price já > esperado`);
                      if (s.fully_paid) reasons.push(`${s.fully_paid} totalmente pago(s)`);
                      if (s.no_base_price) reasons.push(`${s.no_base_price} sem preço base`);
                      const gp = dry.data.resolved_global_pct;
                      toast.error(`Nada para actualizar (scanned=${dry.data.scanned}). Global pct: ${gp}%. Motivos: ${reasons.join(" · ") || "n/d"}`, { duration: 8000 });
                      return;
                    }
                    const details = (dry.data.plans || []).slice(0, 5).map((p) => `• ${p.ticket_number}: ${p.old_final}€ → ${p.new_final}€ (+${p.delta}€)`).join("\n");
                    const more = n > 5 ? `\n… e mais ${n - 5}` : "";
                    if (!window.confirm(`Aplicar surcharge (${dry.data.plans?.[0]?.surcharge_pct || 3}%) a ${n} plano(s)?\n\n${details}${more}\n\nPrestações pagas ficam intactas; as não pagas são redistribuídas.`)) return;
                    const applyBody = forced !== null && !Number.isNaN(forced) ? { pct: forced } : {};
                    const r = await axios.post(`${API_URL}/api/admin/plans/apply-installment-surcharge`, applyBody, { withCredentials: true });
                    toast.success(`Surcharge aplicado a ${r.data.updated} plano(s)`);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
                }}
                data-testid="apply-installment-surcharge-btn"
                title="Aplicar retroactivamente o agravamento de Pagamento Faseado aos planos que ainda não o têm"
              >
                Aplicar % Retroactivo
              </Button>
              <p className="text-xs text-zinc-500">
                {bulkInstFieldsToSend.size === 0
                  ? "Nenhum campo seleccionado — nada será aplicado."
                  : `${bulkInstFieldsToSend.size} campo(s) seleccionado(s) × ${ticketTypes.length} tipo(s) de bilhete`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="border-white/10 text-zinc-200" onClick={() => setBulkInstOpen(false)}>Cancelar</Button>
              <Button className="btn-primary" onClick={applyBulkInstallments} disabled={bulkInstBusy || bulkInstFieldsToSend.size === 0} data-testid="bulk-installments-apply">
                {bulkInstBusy ? "A aplicar..." : "Aplicar a Todos"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Clone Session Dialog */}
      <Dialog open={!!cloneSessionSrc} onOpenChange={(v) => { if (!v) { setCloneSessionSrc(null); setCloneSessionPayload(null); } }}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Duplicar Sessão</DialogTitle>
          </DialogHeader>
          {cloneSessionPayload && (
            <div className="space-y-3 mt-4">
              <p className="text-xs text-zinc-500">
                Cria uma nova sessão a partir de <span className="text-zinc-300 font-medium">{cloneSessionSrc?.name || "(sem nome)"}</span>. Os contadores começam a zero.
              </p>
              {rooms.length > 0 && (
                <div>
                  <Label className="text-zinc-300">Sala</Label>
                  <Select
                    value={cloneSessionPayload.room_id || "__NONE__"}
                    onValueChange={(v) => setCloneSessionPayload({ ...cloneSessionPayload, room_id: v === "__NONE__" ? null : v })}
                  >
                    <SelectTrigger className="input-dark mt-2" data-testid="clone-session-room">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="__NONE__" className="text-zinc-400">— Sem sala —</SelectItem>
                      {rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id} className="text-white">{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-zinc-300">Nome da nova sessão</Label>
                <Input
                  value={cloneSessionPayload.name}
                  onChange={(e) => setCloneSessionPayload({ ...cloneSessionPayload, name: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="clone-session-name"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Subtítulo</Label>
                <Input
                  value={cloneSessionPayload.subtitle}
                  onChange={(e) => setCloneSessionPayload({ ...cloneSessionPayload, subtitle: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="clone-session-subtitle"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300">Data</Label>
                  <Input
                    type="date"
                    value={cloneSessionPayload.date}
                    onChange={(e) => setCloneSessionPayload({ ...cloneSessionPayload, date: e.target.value })}
                    className="input-dark mt-2"
                    data-testid="clone-session-date"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Hora</Label>
                  <Input
                    type="time"
                    value={cloneSessionPayload.time}
                    onChange={(e) => setCloneSessionPayload({ ...cloneSessionPayload, time: e.target.value })}
                    className="input-dark mt-2"
                    data-testid="clone-session-time"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Lugares totais</Label>
                <Input
                  type="number"
                  min="1"
                  value={cloneSessionPayload.total_seats}
                  onChange={(e) => setCloneSessionPayload({ ...cloneSessionPayload, total_seats: parseInt(e.target.value, 10) || 0 })}
                  className="input-dark mt-2"
                  data-testid="clone-session-seats"
                />
              </div>
              <label className="flex items-center gap-3 bg-white/[0.03] rounded-lg px-3 py-2.5 cursor-pointer">
                <Checkbox
                  checked={!!cloneSessionPayload.include_ticket_types}
                  onCheckedChange={(v) => setCloneSessionPayload({ ...cloneSessionPayload, include_ticket_types: !!v })}
                  data-testid="clone-session-include-tt"
                />
                <span className="text-sm text-zinc-200">Incluir tipos de bilhete da sessão original</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => { setCloneSessionSrc(null); setCloneSessionPayload(null); }}>
                  Cancelar
                </Button>
                <Button
                  onClick={submitCloneSession}
                  disabled={cloningSession}
                  className="btn-primary"
                  data-testid="clone-session-confirm"
                >
                  {cloningSession ? "A duplicar..." : "Duplicar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Session Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Criar Nova Sessão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {rooms.length > 0 && (
              <div>
                <Label className="text-zinc-300">Sala (opcional)</Label>
                <Select
                  value={newSession.room_id || "__NONE__"}
                  onValueChange={(v) => setNewSession({ ...newSession, room_id: v === "__NONE__" ? null : v })}
                >
                  <SelectTrigger className="input-dark mt-2" data-testid="new-session-room">
                    <SelectValue placeholder="Sem sala" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="__NONE__" className="text-zinc-400">— Sem sala —</SelectItem>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id} className="text-white">{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Nome da Sessão <span className="text-zinc-500 text-xs">(opcional)</span></Label>
                <Input
                  value={newSession.name}
                  onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                  placeholder="Ex: 1ª Sessão · Manhã"
                  className="input-dark mt-2"
                  data-testid="new-session-name"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newSession.show_name !== false}
                  onCheckedChange={(c) => setNewSession({ ...newSession, show_name: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="new-session-toggle-show_name"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Subtítulo <span className="text-zinc-500 text-xs">(opcional)</span></Label>
                <Input
                  value={newSession.subtitle}
                  onChange={(e) => setNewSession({ ...newSession, subtitle: e.target.value })}
                  placeholder="Ex: KID EXTERNO"
                  className="input-dark mt-2"
                  data-testid="new-session-subtitle"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newSession.show_subtitle !== false}
                  onCheckedChange={(c) => setNewSession({ ...newSession, show_subtitle: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="new-session-toggle-show_subtitle"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Data</Label>
                <Input
                  type="date"
                  value={(newSession.date || "").slice(0, 10)}
                  onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="new-session-date"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newSession.show_date !== false}
                  onCheckedChange={(c) => setNewSession({ ...newSession, show_date: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="new-session-toggle-show_date"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Horário</Label>
                <Input
                  type="time"
                  value={newSession.time}
                  onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="new-session-time"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newSession.show_time !== false}
                  onCheckedChange={(c) => setNewSession({ ...newSession, show_time: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="new-session-toggle-show_time"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Lugares Totais</Label>
                <Input
                  type="number"
                  value={newSession.total_seats}
                  onChange={(e) => setNewSession({ ...newSession, total_seats: parseInt(e.target.value) })}
                  className="input-dark mt-2"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newSession.show_total_seats !== false}
                  onCheckedChange={(c) => setNewSession({ ...newSession, show_total_seats: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="new-session-toggle-show_total_seats"
                />
              </div>
            </div>
            <Button onClick={handleCreateSession} className="w-full btn-primary">
              Criar Sessão
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== TICKET TYPE LIST ====================

const TicketTypeList = ({ sessionId, onUpdate }) => {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  // Clone dialog state
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSourceIds, setCloneSourceIds] = useState([]); // list of TT ids to clone
  const [cloneTargets, setCloneTargets] = useState([]); // list of target session ids
  const [allSessions, setAllSessions] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [cloning, setCloning] = useState(false);
  const defaultInstallments = {
    installments_enabled: false,
    min_first_payment_percent: 25,
    min_installment_amount: 10,
    installment_deadline_type: "before_session",
    installment_deadline_days: 7,
    seat_lock_policy: "from_first",
    suggested_plans: [2, 3, 4],
    cancellation_policy: "partial_refund",
    cancellation_fee_percent: 10,
    expired_policy: "auto_cancel",
    reminder_days_before: [7, 3, 1],
  };
  const [newType, setNewType] = useState({ name: "", price: 0, limit: -1, active: true, show_name: true, show_price: true, show_availability: true, ...defaultInstallments });

  useEffect(() => {
    fetchTypes();
  }, [sessionId]);

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/ticket-types`, { withCredentials: true });
      setTicketTypes(response.data.filter((t) => t.session_id === sessionId));
    } catch (error) {
      console.error("Error fetching ticket types:", error);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(
        `${API_URL}/api/admin/ticket-types`,
        { ...newType, session_id: sessionId },
        { withCredentials: true }
      );
      toast.success("Tipo de bilhete criado!");
      setIsCreateOpen(false);
      setNewType({ name: "", price: 0, limit: -1, active: true, show_name: true, show_price: true, show_availability: true, ...defaultInstallments });
      fetchTypes();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar tipo de bilhete");
    }
  };

  const handleUpdate = async (type) => {
    try {
      const payload = {
        name: type.name,
        price: type.price,
        limit: type.limit,
        active: type.active,
        installments_enabled: !!type.installments_enabled,
        min_first_payment_percent: type.min_first_payment_percent ?? 25,
        installment_surcharge_pct: type.installment_surcharge_pct ?? null,
        min_installment_amount: type.min_installment_amount ?? 10,
        installment_deadline_type: type.installment_deadline_type || "before_session",
        installment_deadline_days: type.installment_deadline_days ?? 7,
        seat_lock_policy: type.seat_lock_policy || "from_first",
        suggested_plans: type.suggested_plans || [2, 3, 4],
        cancellation_policy: type.cancellation_policy || "partial_refund",
        cancellation_fee_percent: type.cancellation_fee_percent ?? 10,
        expired_policy: type.expired_policy || "auto_cancel",
        reminder_days_before: type.reminder_days_before || [7, 3, 1],
        // Parental authorisation config
        requires_parental_auth: !!type.requires_parental_auth,
        age_category: type.age_category || null,
        parental_categories: Array.isArray(type.parental_categories) ? type.parental_categories : [],
        age_min: type.age_min ?? null,
        age_max: type.age_max ?? null,
      };
      await axios.put(
        `${API_URL}/api/admin/ticket-types/${type.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success("Tipo de bilhete atualizado!");
      setEditingType(null);
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const handleDelete = async (typeId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/ticket-types/${typeId}`, { withCredentials: true });
      toast.success("Tipo de bilhete eliminado!");
      fetchTypes();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar");
    }
  };

  const moveType = async (typeId, direction) => {
    try {
      await axios.put(`${API_URL}/api/admin/ticket-types/${typeId}/move-${direction}`, {}, { withCredentials: true });
      fetchTypes();
    } catch (e) { toast.error("Erro ao reordenar"); }
  };

  const toggleTypeVisibility = async (type) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/ticket-types/${type.id}`,
        { active: !type.active },
        { withCredentials: true },
      );
      toast.success(type.active
        ? "Tipo oculto — deixa de aparecer no checkout público"
        : "Tipo visível — volta a aparecer no checkout público");
      fetchTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openCloneDialog = async (sourceIds) => {
    setCloneSourceIds(sourceIds);
    setCloneTargets([]);
    setCloneOpen(true);
    try {
      const [sRes, rRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sessions/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rooms`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAllSessions(Array.isArray(sRes.data) ? sRes.data : []);
      setAllRooms(Array.isArray(rRes.data) ? rRes.data : []);
    } catch (e) {
      toast.error("Erro ao carregar sessões");
    }
  };

  const handleConfirmClone = async () => {
    if (cloneTargets.length === 0) {
      toast.error("Selecione pelo menos uma sessão de destino");
      return;
    }
    setCloning(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/ticket-types/clone`,
        { source_ids: cloneSourceIds, target_session_ids: cloneTargets },
        { withCredentials: true }
      );
      toast.success(`${r.data.created_count} bilhete(s) clonado(s) para ${cloneTargets.length} sessão(ões)`);
      setCloneOpen(false);
      fetchTypes();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao clonar");
    } finally {
      setCloning(false);
    }
  };


  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Ticket className="w-4 h-4 text-amber-500" />
          Tipos de Bilhete
        </h4>
        <div className="flex items-center gap-2">
          {ticketTypes.length > 0 && (
            <Button
              onClick={() => openCloneDialog(ticketTypes.map((t) => t.id))}
              size="sm"
              variant="outline"
              className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
              data-testid={`tt-clone-all-${sessionId}`}
              title="Clonar todos os bilhetes para outras sessões"
            >
              <Copy className="w-4 h-4 mr-1" />
              Clonar todos
            </Button>
          )}
          <Button onClick={() => setIsCreateOpen(true)} size="sm" className="btn-secondary" data-testid={`tt-add-${sessionId}`}>
            <Plus className="w-4 h-4 mr-1" />
            Adicionar
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {ticketTypes.map((type) => (
          <div
            key={type.id}
            className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className={`w-2 h-2 rounded-full ${type.active ? "bg-green-500" : "bg-zinc-500"}`} />
              <span className="text-white">{type.name}</span>
              <span className="text-amber-500 font-medium">{type.price.toFixed(2)}€</span>
              <span className="text-zinc-500 text-sm">
                {type.limit === -1 ? "Ilimitado" : `${type.sold}/${type.limit}`}
              </span>
              {type.installments_enabled && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  Faseado
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <div className="flex flex-col gap-0.5 mr-1" title="Reordenar">
                <button
                  onClick={() => moveType(type.id, "up")}
                  className="p-0.5 rounded hover:bg-white/10 text-zinc-400 hover:text-amber-400 transition"
                  data-testid={`move-up-type-${type.id}`}
                  aria-label="Mover para cima"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveType(type.id, "down")}
                  className="p-0.5 rounded hover:bg-white/10 text-zinc-400 hover:text-amber-400 transition"
                  data-testid={`move-down-type-${type.id}`}
                  aria-label="Mover para baixo"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <Button
                onClick={() => toggleTypeVisibility(type)}
                size="sm"
                variant="ghost"
                className={type.active ? "text-emerald-300 hover:text-emerald-200" : "text-zinc-500 hover:text-zinc-300"}
                data-testid={`tt-toggle-${type.id}`}
                title={type.active ? "Ocultar do checkout público (dados no Dashboard/Finanças mantêm-se)" : "Voltar a mostrar no checkout público"}
              >
                {type.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => openCloneDialog([type.id])}
                size="sm"
                variant="ghost"
                className="text-violet-300 hover:text-violet-200"
                data-testid={`tt-clone-${type.id}`}
                title="Clonar este bilhete para outras sessões"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setEditingType({
                  ...{
                    installments_enabled: false,
                    min_first_payment_percent: 25,
                    min_installment_amount: 10,
                    installment_deadline_type: "before_session",
                    installment_deadline_days: 7,
                    seat_lock_policy: "from_first",
                    suggested_plans: [2, 3, 4],
                    cancellation_policy: "partial_refund",
                    cancellation_fee_percent: 10,
                    expired_policy: "auto_cancel",
                    reminder_days_before: [7, 3, 1],
                  },
                  ...type,
                })}
                size="sm"
                variant="ghost"
                className="text-amber-500"
                data-testid={`tt-edit-${type.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Eliminar Tipo de Bilhete</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Tem a certeza que deseja eliminar este tipo de bilhete?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(type.id)}
                      className="bg-red-500 text-white hover:bg-red-600"
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {ticketTypes.length === 0 && (
          <p className="text-zinc-500 text-sm text-center py-4">Nenhum tipo de bilhete. Adicione um.</p>
        )}
      </div>

      {/* Create Type Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Tipo de Bilhete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Nome</Label>
                <Input
                  value={newType.name}
                  onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                  placeholder="Ex: VIP, Normal, Criança"
                  className="input-dark mt-2"
                  data-testid="tt-create-name"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newType.show_name !== false}
                  onCheckedChange={(c) => setNewType({ ...newType, show_name: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="tt-create-toggle-show_name"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Preço (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newType.price}
                  onChange={(e) => setNewType({ ...newType, price: parseFloat(e.target.value) })}
                  className="input-dark mt-2"
                  data-testid="tt-create-price"
                />
              </div>
              <div className="flex flex-col items-center gap-1 pb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                <Switch
                  checked={newType.show_price !== false}
                  onCheckedChange={(c) => setNewType({ ...newType, show_price: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="tt-create-toggle-show_price"
                />
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-zinc-300">Limite de Bilhetes</Label>
                <Input
                  type="number"
                  min="-1"
                  value={newType.limit}
                  onChange={(e) => setNewType({ ...newType, limit: parseInt(e.target.value) || -1 })}
                  placeholder="-1 para ilimitado"
                  className="input-dark mt-2"
                />
                <p className="text-zinc-500 text-xs mt-1">Use -1 para quantidade ilimitada</p>
              </div>
              <div className="flex flex-col items-center gap-1 pb-7" title="Mostrar quantos lugares ainda estão disponíveis para este tipo de bilhete">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider text-center leading-tight">Público<br/>(disponíveis)</span>
                <Switch
                  checked={newType.show_availability !== false}
                  onCheckedChange={(c) => setNewType({ ...newType, show_availability: c })}
                  className="data-[state=checked]:bg-violet-500"
                  data-testid="tt-create-toggle-show_availability"
                />
              </div>
            </div>

            <InstallmentConfigSection
              value={newType}
              onChange={(v) => setNewType({ ...newType, ...v })}
            />

            <ParentalAuthConfigSection
              value={newType}
              onChange={(v) => setNewType({ ...newType, ...v })}
            />

            <Button onClick={handleCreate} className="w-full btn-primary" data-testid="tt-create-submit">
              Criar Tipo de Bilhete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Type Dialog */}
      <Dialog open={!!editingType} onOpenChange={(v) => !v && setEditingType(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 max-h-[85vh] overflow-y-auto sm:max-w-[590px]">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Tipo de Bilhete</DialogTitle>
          </DialogHeader>
          {editingType && (
            <div className="space-y-4 mt-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-zinc-300">Nome</Label>
                  <Input
                    value={editingType.name}
                    onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                    className="input-dark mt-2"
                    data-testid="tt-edit-name"
                  />
                </div>
                <div className="flex flex-col items-center gap-1 pb-1.5">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                  <Switch
                    checked={editingType.show_name !== false}
                    onCheckedChange={(c) => setEditingType({ ...editingType, show_name: c })}
                    className="data-[state=checked]:bg-violet-500"
                    data-testid="tt-edit-toggle-show_name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Preço (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingType.price}
                      onChange={(e) => setEditingType({ ...editingType, price: parseFloat(e.target.value) })}
                      className="input-dark mt-2"
                      data-testid="tt-edit-price"
                    />
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-1.5">
                    <span className="text-[10px] text-zinc-500 uppercase">Público</span>
                    <Switch
                      checked={editingType.show_price !== false}
                      onCheckedChange={(c) => setEditingType({ ...editingType, show_price: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="tt-edit-toggle-show_price"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Limite</Label>
                    <Input
                      type="number"
                      min="-1"
                      value={editingType.limit}
                      onChange={(e) => setEditingType({ ...editingType, limit: parseInt(e.target.value) || -1 })}
                      className="input-dark mt-2"
                    />
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-1.5" title="Mostrar quantos lugares ainda estão disponíveis">
                    <span className="text-[10px] text-zinc-500 uppercase text-center leading-tight">Público<br/>(disp.)</span>
                    <Switch
                      checked={editingType.show_availability !== false}
                      onCheckedChange={(c) => setEditingType({ ...editingType, show_availability: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="tt-edit-toggle-show_availability"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2.5">
                <Label className="text-zinc-300">Activo</Label>
                <Switch
                  checked={!!editingType.active}
                  onCheckedChange={(v) => setEditingType({ ...editingType, active: v })}
                  data-testid="tt-edit-active"
                />
              </div>

              <InstallmentConfigSection
                value={editingType}
                onChange={(v) => setEditingType({ ...editingType, ...v })}
                defaultOpen={!!editingType.installments_enabled}
              />

              <ParentalAuthConfigSection
                value={editingType}
                onChange={(v) => setEditingType({ ...editingType, ...v })}
                defaultOpen={!!editingType.requires_parental_auth}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => setEditingType(null)}>
                  Cancelar
                </Button>
                <Button onClick={() => handleUpdate(editingType)} className="btn-primary" data-testid="tt-edit-save">
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneOpen} onOpenChange={(v) => !v && setCloneOpen(false)}>
        <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Copy className="w-5 h-5 text-violet-300" />
              Clonar {cloneSourceIds.length === 1 ? "bilhete" : `${cloneSourceIds.length} bilhetes`}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Selecione as sessões de destino. Os bilhetes serão duplicados em cada uma, com contadores a zero.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {(() => {
              // Allow cloning into ANY session, including the current one (useful for
              // quickly duplicating a ticket type within the same session).
              const eligible = allSessions;
              if (eligible.length === 0) {
                return <p className="text-zinc-500 text-sm">Não existem outras sessões disponíveis.</p>;
              }
              const groups = [];
              const seen = new Map();
              eligible.forEach((s) => {
                const key = s.room_id || "__none__";
                if (!seen.has(key)) {
                  seen.set(key, groups.length);
                  const r = s.room_id ? allRooms.find((x) => x.id === s.room_id) : null;
                  groups.push({ key, label: r ? r.name : "(sem sala)", items: [] });
                }
                groups[seen.get(key)].items.push(s);
              });
              const toggle = (sid) =>
                setCloneTargets((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
              const toggleGroup = (items, allOn) => {
                const ids = items.map((i) => i.id);
                setCloneTargets((prev) => {
                  if (allOn) return prev.filter((x) => !ids.includes(x));
                  return Array.from(new Set([...prev, ...ids]));
                });
              };
              return groups.map((g) => {
                const allOn = g.items.every((it) => cloneTargets.includes(it.id));
                return (
                  <div key={g.key} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-violet-300 text-xs uppercase tracking-wider font-semibold">{g.label}</span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.items, allOn)}
                        className="text-xs text-zinc-400 hover:text-white transition"
                      >
                        {allOn ? "Desmarcar todas" : "Selecionar todas"}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {g.items.map((s) => {
                        const checked = cloneTargets.includes(s.id);
                        const parts = [];
                        if (s.name) parts.push(s.name);
                        if (s.subtitle) parts.push(s.subtitle);
                        if (s.date) parts.push(s.date);
                        if (s.time) parts.push(s.time);
                        return (
                          <label
                            key={s.id}
                            className="flex items-center gap-3 cursor-pointer hover:bg-white/5 rounded px-2 py-1.5"
                            data-testid={`clone-target-${s.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggle(s.id)}
                              className="data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500"
                            />
                            <span className="text-white text-sm">
                              {parts.join(" · ") || "(sem nome)"}
                              {s.id === sessionId && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">esta sessão</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/10">
            <Button onClick={() => setCloneOpen(false)} variant="outline" className="border-white/10 text-zinc-300">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmClone}
              disabled={cloning || cloneTargets.length === 0}
              className="btn-primary"
              data-testid="clone-confirm"
            >
              {cloning ? "A clonar..." : `Clonar para ${cloneTargets.length} sessão(ões)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== COUPON MANAGEMENT ====================

export const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: 10,
    max_uses: -1,
    valid_until: "",
    active: true,
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [usagesModal, setUsagesModal] = useState(null); // { code, uses_stored, uses_actual, registrations:[...] }
  const [loadingUsages, setLoadingUsages] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/coupons`, { withCredentials: true });
      setCoupons(response.data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/coupons`, newCoupon, { withCredentials: true });
      toast.success("Cupão criado com sucesso!");
      setIsCreateOpen(false);
      setNewCoupon({
        code: "",
        discount_type: "percentage",
        discount_value: 10,
        max_uses: -1,
        valid_until: "",
        active: true,
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar cupão");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/coupons/${editingCoupon.id}`,
        editingCoupon,
        { withCredentials: true }
      );
      toast.success("Cupão atualizado!");
      setEditingCoupon(null);
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao atualizar cupão");
    }
  };

  const handleDelete = async (couponId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/coupons/${couponId}`, { withCredentials: true });
      toast.success("Cupão eliminado!");
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao eliminar cupão");
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/coupons/${coupon.id}`,
        { active: !coupon.active },
        { withCredentials: true }
      );
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao atualizar estado");
    }
  };

  const handleReconcile = async () => {
    if (!window.confirm("Reconciliar contadores de utilização a partir dos bilhetes reais? Cupões com discrepância serão corrigidos.")) return;
    setReconciling(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/coupons/reconcile-uses`, {}, { withCredentials: true });
      toast.success(r.data.message);
      if (r.data.corrections?.length > 0) {
        // Show details in console for the admin to review
        console.log("Coupon reconcile corrections:", r.data.corrections);
      }
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reconciliar");
    } finally { setReconciling(false); }
  };

  const openUsages = async (coupon) => {
    setLoadingUsages(true);
    setUsagesModal({ code: coupon.code, uses_stored: coupon.uses, uses_actual: 0, registrations: [] });
    try {
      const r = await axios.get(`${API_URL}/api/admin/coupons/${coupon.id}/usages`, { withCredentials: true });
      setUsagesModal(r.data);
    } catch (e) {
      toast.error("Erro ao carregar utilizações");
      setUsagesModal(null);
    } finally { setLoadingUsages(false); }
  };

  if (loading) return <div className="animate-pulse">A carregar...</div>;

  return (
    <div className="space-y-6" data-testid="coupon-management">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Cupões de Desconto</h3>
          <p className="text-zinc-500 text-sm">Crie cupões com percentagem ou valor fixo</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleReconcile} disabled={reconciling} variant="outline" className="border-white/10 text-zinc-300" data-testid="btn-reconcile-coupons" title="Recalcula o contador de utilizações a partir dos bilhetes reais">
            {reconciling ? "A reconciliar..." : "Reconciliar"}
          </Button>
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-white/10 text-zinc-300" data-testid="btn-open-bulk-coupons">
            <Gift className="w-4 h-4 mr-2" />
            Lote de Cupões
          </Button>
          <Button onClick={() => setIsCreateOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cupão
          </Button>
        </div>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="table-dark">
          <thead>
            <tr>
              <th>Código</th>
              <th>Desconto</th>
              <th>Utilizações</th>
              <th>Validade</th>
              <th>Estado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => (
              <tr key={coupon.id}>
                <td className="font-mono text-amber-500 font-bold">{coupon.code}</td>
                <td className="text-white">
                  {coupon.discount_type === "percentage" ? (
                    <span className="flex items-center gap-1">
                      <Percent className="w-4 h-4" />
                      {coupon.discount_value}%
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {coupon.discount_value}€
                    </span>
                  )}
                </td>
                <td className="text-zinc-400">
                  {coupon.uses > 0 ? (
                    <button
                      type="button"
                      onClick={() => openUsages(coupon)}
                      className="text-amber-400 underline decoration-dotted hover:text-amber-300 hover:decoration-solid"
                      data-testid={`btn-coupon-usages-${coupon.id}`}
                      title="Ver bilhetes que usaram este cupão"
                    >
                      {coupon.uses} / {coupon.max_uses === -1 ? "∞" : coupon.max_uses}
                    </button>
                  ) : (
                    <>{coupon.uses} / {coupon.max_uses === -1 ? "∞" : coupon.max_uses}</>
                  )}
                </td>
                <td className="text-zinc-400">
                  {coupon.valid_until || "Sem limite"}
                </td>
                <td>
                  <Switch
                    checked={coupon.active}
                    onCheckedChange={() => toggleActive(coupon)}
                  />
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => setEditingCoupon(coupon)}
                      size="sm"
                      variant="ghost"
                      className="text-amber-500"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Eliminar Cupão</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            Tem a certeza que deseja eliminar este cupão?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(coupon.id)}
                            className="bg-red-500 text-white hover:bg-red-600"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {coupons.length === 0 && (
          <div className="text-center py-12 text-zinc-500">Nenhum cupão criado</div>
        )}
      </div>

      {/* Create Coupon Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Criar Novo Cupão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Código do Cupão</Label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                placeholder="Ex: DESCONTO10"
                className="input-dark mt-2 font-mono"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Tipo de Desconto</Label>
              <Select
                value={newCoupon.discount_type}
                onValueChange={(value) => setNewCoupon({ ...newCoupon, discount_type: value })}
              >
                <SelectTrigger className="input-dark mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="percentage" className="text-white">Percentagem (%)</SelectItem>
                  <SelectItem value="fixed" className="text-white">Valor Fixo (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">
                Valor do Desconto {newCoupon.discount_type === "percentage" ? "(%)" : "(€)"}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={newCoupon.discount_value}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: parseFloat(e.target.value) })}
                className="input-dark mt-2"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Limite de Utilizações</Label>
              <Input
                type="number"
                value={newCoupon.max_uses}
                onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) })}
                placeholder="-1 para ilimitado"
                className="input-dark mt-2"
              />
              <p className="text-zinc-500 text-xs mt-1">Use -1 para utilizações ilimitadas</p>
            </div>
            <div>
              <Label className="text-zinc-300">Válido Até (opcional)</Label>
              <Input
                type="date"
                value={newCoupon.valid_until}
                onChange={(e) => setNewCoupon({ ...newCoupon, valid_until: e.target.value })}
                className="input-dark mt-2"
              />
            </div>
            <Button onClick={handleCreate} className="w-full btn-primary">
              Criar Cupão
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Coupon Dialog */}
      <Dialog open={!!editingCoupon} onOpenChange={() => setEditingCoupon(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Cupão</DialogTitle>
          </DialogHeader>
          {editingCoupon && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-300">Código do Cupão</Label>
                <Input
                  value={editingCoupon.code}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, code: e.target.value.toUpperCase() })}
                  className="input-dark mt-2 font-mono"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Tipo de Desconto</Label>
                <Select
                  value={editingCoupon.discount_type}
                  onValueChange={(value) => setEditingCoupon({ ...editingCoupon, discount_type: value })}
                >
                  <SelectTrigger className="input-dark mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="percentage" className="text-white">Percentagem (%)</SelectItem>
                    <SelectItem value="fixed" className="text-white">Valor Fixo (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Valor do Desconto</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingCoupon.discount_value}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_value: parseFloat(e.target.value) })}
                  className="input-dark mt-2"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Limite de Utilizações</Label>
                <Input
                  type="number"
                  value={editingCoupon.max_uses}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, max_uses: parseInt(e.target.value) })}
                  className="input-dark mt-2"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Válido Até</Label>
                <Input
                  type="date"
                  value={editingCoupon.valid_until || ""}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, valid_until: e.target.value })}
                  className="input-dark mt-2"
                />
              </div>
              <Button onClick={handleUpdate} className="w-full btn-primary">
                Guardar Alterações
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Coupons Dialog */}
      <BulkCouponDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onCreated={fetchCoupons} />

      {/* Coupon Usages Modal */}
      <Dialog open={!!usagesModal} onOpenChange={(v) => { if (!v) setUsagesModal(null); }}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="coupon-usages-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Utilizações do cupão <span className="font-mono text-amber-400">{usagesModal?.code}</span>
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {usagesModal && (
                <>
                  Contador na BD: <strong>{usagesModal.uses_stored}</strong> · Bilhetes reais: <strong>{usagesModal.uses_actual}</strong>
                  {usagesModal.uses_stored !== usagesModal.uses_actual && (
                    <span className="text-amber-300 ml-2">⚠️ discrepância — clique em "Reconciliar" para corrigir</span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {loadingUsages ? (
            <div className="text-zinc-500 text-sm py-4">A carregar...</div>
          ) : usagesModal && usagesModal.registrations.length === 0 ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-100 text-sm">
              Nenhum bilhete real encontrado. O contador está inflacionado — provavelmente por reservas pendentes apagadas. Clique em "Reconciliar" para corrigir.
            </div>
          ) : (
            <div className="bg-zinc-950 border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900 text-zinc-400">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Nome</th>
                    <th className="text-left px-3 py-2 font-medium">Email</th>
                    <th className="text-left px-3 py-2 font-medium">Estado</th>
                    <th className="text-left px-3 py-2 font-medium">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {usagesModal?.registrations.map((r) => (
                    <tr key={r.id} className="border-t border-white/5">
                      <td className="px-3 py-2 text-zinc-200">{r.first_name} {r.last_name}</td>
                      <td className="px-3 py-2 text-zinc-200 break-all">{r.email}</td>
                      <td className="px-3 py-2">
                        <span className={r.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}>
                          {r.payment_status === "paid" ? "Pago" : (r.payment_status || "pendente")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-zinc-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString("pt-PT") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== BULK COUPONS (GIFT CAMPAIGN) ====================

const BulkCouponDialog = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(1); // 1=create, 2=review, 3=send
  const [sourceMode, setSourceMode] = useState("manual"); // manual | buyers | group
  const [emailsText, setEmailsText] = useState("");
  const [singleEmail, setSingleEmail] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loadingSource, setLoadingSource] = useState(false);
  const [prefix, setPrefix] = useState("XTREMO-");
  const [discountValue, setDiscountValue] = useState(100);
  const [validUntil, setValidUntil] = useState("");
  const [creating, setCreating] = useState(false);
  const [batchResult, setBatchResult] = useState(null); // { batch_id, coupons:[...] }
  const [pastBatches, setPastBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [subject, setSubject] = useState("🎁 O seu bilhete grátis para o XTREMO");
  const [bodyHtml, setBodyHtml] = useState(
    `<div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;padding:24px;color:#222;">
  <h2 style="color:#f59e0b;">Obrigado por estar connosco!</h2>
  <p>Como reconhecimento pela sua presença e apoio, oferecemos-lhe <strong>1 bilhete grátis</strong> para a próxima sessão do XTREMO.</p>
  <p>Use o seguinte cupão no checkout:</p>
  <p style="font-size:28px;text-align:center;font-family:monospace;color:#f59e0b;border:2px dashed #f59e0b;padding:14px;border-radius:8px;">{{coupon_code}}</p>
  <p style="text-align:center;margin:24px 0;">
    <a href="{{coupon_link}}" style="background:#f59e0b;color:#000;font-weight:bold;text-decoration:none;padding:14px 28px;border-radius:8px;">Reservar agora</a>
  </p>
  <p style="color:#666;font-size:13px;">Cupão válido apenas para o email <strong>{{email}}</strong>. Apenas 1 utilização.</p>
</div>`
  );
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const reset = () => {
    setStep(1); setEmailsText(""); setSingleEmail(""); setSourceMode("manual");
    setSelectedGroup(""); setPrefix("XTREMO-"); setDiscountValue(100);
    setValidUntil(""); setBatchResult(null); setSendResult(null);
  };

  const parsedEmails = (() => {
    if (sourceMode === "manual" && singleEmail.trim() && !emailsText.trim()) {
      const e = singleEmail.trim().toLowerCase();
      return e.includes("@") ? [e] : [];
    }
    return emailsText.split(/[\n,;]/).map(e => e.trim().toLowerCase()).filter(e => e && e.includes("@"));
  })();

  const loadGroups = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/groups`, { withCredentials: true });
      setGroups(r.data || []);
    } catch (_) { setGroups([]); }
  };

  const loadSourceEmails = async (mode, groupCode = "") => {
    setLoadingSource(true);
    try {
      const params = mode === "group"
        ? { source: "group", group_code: groupCode }
        : { source: "buyers" };
      const r = await axios.get(`${API_URL}/api/admin/coupons/source-emails`, { params, withCredentials: true });
      const list = r.data?.emails || [];
      setEmailsText(list.join("\n"));
      if (list.length === 0) {
        toast.info(mode === "group" ? "Nenhum comprador encontrado neste grupo" : "Nenhum comprador encontrado");
      } else {
        toast.success(`${list.length} email(s) carregado(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar emails");
    } finally { setLoadingSource(false); }
  };

  const loadPastBatches = async () => {
    setLoadingBatches(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/coupons/batches`, { withCredentials: true });
      setPastBatches(r.data || []);
    } catch (_) {
      setPastBatches([]);
    } finally { setLoadingBatches(false); }
  };

  useEffect(() => {
    if (open) { loadPastBatches(); loadGroups(); }
  }, [open]);

  const openExistingBatch = async (batchId) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/coupons/batch/${batchId}`, { withCredentials: true });
      const coupons = (r.data || []).map((c) => ({ email: c.assigned_email, code: c.code, id: c.id }));
      setBatchResult({ batch_id: batchId, created: coupons.length, coupons });
      setStep(2);
    } catch (e) {
      toast.error("Erro ao carregar lote");
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm("Eliminar os cupões NÃO UTILIZADOS deste lote? (cupões já usados são preservados)")) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/coupons/batch/${batchId}`, { withCredentials: true });
      toast.success(r.data.message || "Lote eliminado");
      loadPastBatches();
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao eliminar lote");
    }
  };

  const createBatch = async () => {
    if (parsedEmails.length === 0) { toast.error("Cola pelo menos 1 email válido"); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/coupons/bulk-create`, {
        emails: parsedEmails,
        discount_type: "percentage",
        discount_value: discountValue,
        prefix: prefix,
        valid_until: validUntil || null,
      }, { withCredentials: true });
      setBatchResult(r.data);
      setStep(2);
      onCreated && onCreated();
      loadPastBatches();
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar cupões");
    } finally { setCreating(false); }
  };

  const sendEmails = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/coupons/send-batch`, {
        batch_id: batchResult.batch_id,
        subject,
        body_html: bodyHtml,
        only_unused: true,
      }, { withCredentials: true });
      setSendResult(r.data);
      setStep(3);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar emails");
    } finally { setSending(false); }
  };

  const exportCsv = () => {
    if (!batchResult?.coupons?.length) return;
    const rows = ["email,codigo", ...batchResult.coupons.map(c => `${c.email},${c.code}`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cupoes-${batchResult.batch_id.slice(0, 8)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-coupon-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            Lote de Cupões — Oferta Personalizada
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {step === 1 && "Cole a lista de emails. Cada destinatário receberá um cupão único e personalizado."}
            {step === 2 && "Reveja os cupões e personalize o email antes de enviar."}
            {step === 3 && "Envio concluído."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            {pastBatches.length > 0 && (
              <div className="bg-zinc-950 border border-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-300 text-sm">Lotes existentes</Label>
                  {loadingBatches && <span className="text-zinc-500 text-xs">A carregar...</span>}
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1.5">
                  {pastBatches.map((b) => (
                    <div key={b.batch_id} className="flex items-center justify-between bg-zinc-900 rounded px-2 py-1.5 text-xs" data-testid={`past-batch-${b.batch_id.slice(0,8)}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-amber-400 truncate">{b.first_code}…</div>
                        <div className="text-zinc-500">
                          {new Date(b.created_at).toLocaleString("pt-PT")} ·
                          {" "}{b.total} cupões · {b.used} usados · {b.unused} disponíveis
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="outline" className="h-7 border-white/10 text-zinc-300" onClick={() => openExistingBatch(b.batch_id)} data-testid={`btn-open-batch-${b.batch_id.slice(0,8)}`}>
                          Abrir
                        </Button>
                        {b.unused > 0 && (
                          <Button size="sm" variant="outline" className="h-7 border-red-500/30 text-red-300 hover:bg-red-500/10" onClick={() => deleteBatch(b.batch_id)}>
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Source mode tabs */}
            <div>
              <Label className="text-zinc-300 text-sm">Origem dos Destinatários</Label>
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="source-tabs">
                <Button
                  type="button"
                  variant={sourceMode === "manual" ? "default" : "outline"}
                  className={sourceMode === "manual" ? "btn-primary" : "border-white/10 text-zinc-300"}
                  onClick={() => setSourceMode("manual")}
                  data-testid="src-manual"
                >
                  ✍️ Manual
                </Button>
                <Button
                  type="button"
                  variant={sourceMode === "buyers" ? "default" : "outline"}
                  className={sourceMode === "buyers" ? "btn-primary" : "border-white/10 text-zinc-300"}
                  onClick={() => { setSourceMode("buyers"); loadSourceEmails("buyers"); }}
                  data-testid="src-buyers"
                >
                  🛒 Compradores
                </Button>
                <Button
                  type="button"
                  variant={sourceMode === "group" ? "default" : "outline"}
                  className={sourceMode === "group" ? "btn-primary" : "border-white/10 text-zinc-300"}
                  onClick={() => setSourceMode("group")}
                  data-testid="src-group"
                >
                  👥 Por Grupo
                </Button>
              </div>
            </div>

            {sourceMode === "manual" && (
              <div className="grid grid-cols-2 gap-3" data-testid="manual-source-block">
                <div>
                  <Label className="text-zinc-300">Email único (1 a 1)</Label>
                  <Input
                    type="email"
                    value={singleEmail}
                    onChange={(e) => setSingleEmail(e.target.value)}
                    placeholder="destinatario@email.com"
                    className="input-dark mt-2"
                    data-testid="bulk-single-email"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Use para enviar apenas 1 cupão a 1 pessoa.</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Ou cole vários emails</Label>
                  <textarea
                    value={emailsText}
                    onChange={(e) => setEmailsText(e.target.value)}
                    rows={4}
                    className="w-full mt-2 input-dark font-mono text-sm"
                    placeholder={"maria@email.com\njoao@email.com"}
                    data-testid="bulk-emails-textarea"
                  />
                </div>
              </div>
            )}

            {sourceMode === "group" && (
              <div data-testid="group-source-block">
                <Label className="text-zinc-300">Grupo</Label>
                <div className="flex gap-2 mt-2">
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="flex-1 input-dark"
                    data-testid="bulk-group-select"
                  >
                    <option value="">— Selecionar grupo —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.code}>
                        {`${g.name} (${g.code}) · ${g.members_count || 0} compradores`}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={() => selectedGroup && loadSourceEmails("group", selectedGroup)}
                    disabled={!selectedGroup || loadingSource}
                    className="btn-primary"
                    data-testid="btn-load-group-emails"
                  >
                    {loadingSource ? "A carregar..." : "Carregar"}
                  </Button>
                </div>
                {groups.length === 0 && (
                  <p className="text-amber-300 text-xs mt-2">Nenhum grupo criado. Crie um em Configurações → Grupos.</p>
                )}
              </div>
            )}

            {sourceMode !== "manual" && (
              <div>
                <Label className="text-zinc-300">Emails carregados (editáveis)</Label>
                <textarea
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  rows={6}
                  className="w-full mt-2 input-dark font-mono text-sm"
                  placeholder="Carregue compradores ou um grupo acima"
                  data-testid="bulk-emails-textarea-loaded"
                />
              </div>
            )}

            <p className="text-zinc-500 text-xs">{parsedEmails.length} email(s) válido(s) detectado(s)</p>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-300">Prefixo</Label>
                <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} className="input-dark mt-2" data-testid="bulk-prefix" />
              </div>
              <div>
                <Label className="text-zinc-300">Desconto %</Label>
                <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(parseFloat(e.target.value))} className="input-dark mt-2" min={1} max={100} data-testid="bulk-discount" />
              </div>
              <div>
                <Label className="text-zinc-300">Válido até</Label>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="input-dark mt-2" data-testid="bulk-valid-until" />
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-100">
              ⚠️ Cada cupão fica <strong>amarrado ao email</strong> — só funciona para quem recebeu. Apenas 1 utilização cada.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => { reset(); onClose(); }} variant="outline" className="border-white/10 text-zinc-300">Cancelar</Button>
              <Button onClick={createBatch} disabled={creating || parsedEmails.length === 0} className="btn-primary" data-testid="btn-bulk-create">
                {creating ? "A gerar..." : `Gerar ${parsedEmails.length} cupão(ões)`}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && batchResult && (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-100">
              ✅ {batchResult.created} cupão(ões) gerado(s). Pode revê-los abaixo ou exportar para CSV antes de enviar os emails.
            </div>
            <div className="max-h-40 overflow-y-auto bg-zinc-950 rounded-lg p-3 text-xs font-mono">
              {batchResult.coupons.slice(0, 10).map((c) => (
                <div key={c.id} className="py-0.5 flex justify-between text-zinc-400"><span>{c.email}</span><span className="text-amber-400">{c.code}</span></div>
              ))}
              {batchResult.coupons.length > 10 && <div className="text-zinc-500 mt-1">…e mais {batchResult.coupons.length - 10}</div>}
            </div>
            <Button onClick={exportCsv} variant="outline" className="border-white/10 text-zinc-300 w-full"><Mail className="w-4 h-4 mr-2" />Exportar CSV (backup)</Button>
            <div>
              <Label className="text-zinc-300">Assunto do Email</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-dark mt-2" data-testid="bulk-subject" />
            </div>
            <div>
              <Label className="text-zinc-300">Corpo HTML do Email</Label>
              <textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={10}
                className="w-full mt-2 input-dark font-mono text-xs"
                data-testid="bulk-body"
              />
              <p className="text-zinc-500 text-xs mt-1">
                Placeholders disponíveis: <code className="text-amber-400">{"{{coupon_code}}"}</code>, <code className="text-amber-400">{"{{email}}"}</code>, <code className="text-amber-400">{"{{coupon_link}}"}</code>
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setStep(1)} variant="outline" className="border-white/10 text-zinc-300">Voltar</Button>
              <Button onClick={sendEmails} disabled={sending} className="btn-primary" data-testid="btn-bulk-send">
                <Send className="w-4 h-4 mr-2" />
                {sending ? "A enviar..." : `Enviar ${batchResult.coupons.length} email(s)`}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && sendResult && (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-emerald-100">
              <div className="text-2xl font-bold">{sendResult.sent} email(s) enviados ✅</div>
              {sendResult.errors > 0 && <div className="text-red-300 mt-1">⚠️ {sendResult.errors} falha(s)</div>}
            </div>
            {sendResult.error_details?.length > 0 && (
              <div className="max-h-32 overflow-y-auto bg-zinc-950 rounded-lg p-3 text-xs">
                {sendResult.error_details.map((e, i) => (
                  <div key={i} className="py-0.5 text-red-300">{e.email}: {e.error}</div>
                ))}
              </div>
            )}
            <Button onClick={() => { reset(); onClose(); }} className="btn-primary w-full">Fechar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== CUSTOM FIELDS MANAGEMENT ====================

export const CustomFieldsManagement = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    name: "",
    label: "",
    field_type: "text",
    options: [],
    allow_other: false,
    required: true,
    active: true,
    placeholder: "",
  });
  const [newOption, setNewOption] = useState("");

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true });
      setFields(response.data);
    } catch (error) {
      console.error("Error fetching fields:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const fieldData = {
        ...newField,
        name: newField.name || newField.label.toLowerCase().replace(/\s+/g, "_"),
      };
      await axios.post(`${API_URL}/api/admin/custom-fields`, fieldData, { withCredentials: true });
      toast.success("Campo criado com sucesso!");
      setIsCreateOpen(false);
      setNewField({
        name: "",
        label: "",
        field_type: "text",
        options: [],
        allow_other: false,
        required: true,
        active: true,
        placeholder: "",
      });
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar campo");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/custom-fields/${editingField.id}`,
        editingField,
        { withCredentials: true }
      );
      toast.success("Campo atualizado!");
      setEditingField(null);
      fetchFields();
    } catch (error) {
      toast.error("Erro ao atualizar campo");
    }
  };

  const handleDelete = async (fieldId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/custom-fields/${fieldId}`, { withCredentials: true });
      toast.success("Campo eliminado!");
      fetchFields();
    } catch (error) {
      toast.error("Erro ao eliminar campo");
    }
  };

  const toggleActive = async (field) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/custom-fields/${field.id}`,
        { active: !field.active },
        { withCredentials: true }
      );
      fetchFields();
    } catch (error) {
      toast.error("Erro ao atualizar estado");
    }
  };

  const moveField = async (index, direction) => {
    const newFields = [...fields];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newFields.length) return;
    [newFields[index], newFields[swapIndex]] = [newFields[swapIndex], newFields[index]];
    setFields(newFields);
    try {
      await axios.put(
        `${API_URL}/api/admin/custom-fields-reorder`,
        { field_ids: newFields.map(f => f.id) },
        { withCredentials: true }
      );
    } catch (error) {
      toast.error("Erro ao reordenar campos");
      fetchFields();
    }
  };

  const addOption = () => {
    if (newOption.trim()) {
      setNewField({ ...newField, options: [...newField.options, newOption.trim()] });
      setNewOption("");
    }
  };

  const removeOption = (index) => {
    setNewField({ ...newField, options: newField.options.filter((_, i) => i !== index) });
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: "Texto",
      number: "Número",
      email: "Email",
      phone: "Telefone",
      date: "Data de Nascimento",
      dropdown: "Lista de Opções",
      checkbox: "Checkbox",
    };
    return types[type] || type;
  };

  if (loading) return <div className="animate-pulse">A carregar...</div>;

  return (
    <div className="space-y-6" data-testid="custom-fields-management">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Campos do Formulário</h3>
          <p className="text-zinc-500 text-sm">Personalize os campos que aparecem no formulário de registo</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Campo
        </Button>
      </div>

      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-200/90 leading-relaxed">
        <strong className="text-blue-300">Como funcionam os campos "Padrão":</strong> Os campos <code className="text-white bg-white/10 px-1 rounded">first_name, last_name, email, phone, church, age</code> são chaves do sistema — cada bilhete guarda o valor destes campos numa coluna dedicada. Pode alterar o <strong>label</strong>, marcar como obrigatório e reordenar, mas <strong>não os elimine</strong> nem mude o nome interno. Na tabela de <em>Gestão de Bilhetes</em> aparecem apenas <strong>uma vez</strong> (na coluna principal), nunca duplicados como campo personalizado.
      </div>

      <div className="space-y-2">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="bg-zinc-900 rounded-lg p-4 border border-white/10 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveField(index, -1)}
                  disabled={index === 0}
                  className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors p-0.5"
                  data-testid={`move-field-up-${field.id}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveField(index, 1)}
                  disabled={index === fields.length - 1}
                  className="text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors p-0.5"
                  data-testid={`move-field-down-${field.id}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <div className={`w-2 h-2 rounded-full ${field.active ? "bg-green-500" : "bg-zinc-500"}`} />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{field.label}</span>
                  {["first_name","last_name","email","phone","church","age"].includes(field.name) && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 font-semibold uppercase tracking-wider"
                      title="Campo padrão do sistema — necessário para o formulário de registo. Pode renomear/reordenar mas não recomendamos eliminar."
                      data-testid={`field-default-badge-${field.name}`}
                    >
                      Padrão
                    </span>
                  )}
                  {field.required && (
                    <span className="text-red-400 text-xs">*obrigatório</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <FormInput className="w-4 h-4" />
                  <span>{getFieldTypeLabel(field.field_type)}</span>
                  {field.field_type === "dropdown" && field.options.length > 0 && (
                    <span>({field.options.length} opções)</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={field.active}
                onCheckedChange={() => toggleActive(field)}
              />
              <Button
                onClick={() => setEditingField(field)}
                size="sm"
                variant="ghost"
                className="text-amber-500"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Eliminar Campo</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Tem a certeza que deseja eliminar este campo?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(field.id)}
                      className="bg-red-500 text-white hover:bg-red-600"
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {fields.length === 0 && (
          <div className="text-center py-12 text-zinc-500">Nenhum campo personalizado</div>
        )}
      </div>

      {/* Create Field Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Criar Novo Campo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Etiqueta (visível ao utilizador)</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="Ex: Nome Completo"
                className="input-dark mt-2"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Nome Técnico (opcional)</Label>
              <Input
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                placeholder="nome_completo"
                className="input-dark mt-2 font-mono"
              />
              <p className="text-zinc-500 text-xs mt-1">Será gerado automaticamente se deixar vazio</p>
            </div>
            <div>
              <Label className="text-zinc-300">Placeholder (texto dentro da caixa)</Label>
              <Input
                value={newField.placeholder}
                onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                placeholder="Ex: Escreva o seu nome..."
                className="input-dark mt-2"
              />
              <p className="text-zinc-500 text-xs mt-1">Texto cinza que aparece dentro do campo e desaparece ao digitar</p>
            </div>
            <div>
              <Label className="text-zinc-300">Tipo de Campo</Label>
              <Select
                value={newField.field_type}
                onValueChange={(value) => setNewField({ ...newField, field_type: value })}
              >
                <SelectTrigger className="input-dark mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="text" className="text-white">Texto</SelectItem>
                  <SelectItem value="number" className="text-white">Número</SelectItem>
                  <SelectItem value="email" className="text-white">Email</SelectItem>
                  <SelectItem value="phone" className="text-white">Telefone</SelectItem>
                  <SelectItem value="date" className="text-white">Data de Nascimento</SelectItem>
                  <SelectItem value="dropdown" className="text-white">Lista de Opções</SelectItem>
                  <SelectItem value="checkbox" className="text-white">Checkbox (Sim/Não)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newField.field_type === "dropdown" && (
              <div>
                <Label className="text-zinc-300">Opções</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Adicionar opção"
                    className="input-dark"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
                  />
                  <Button onClick={addOption} type="button" className="btn-secondary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {newField.options.map((opt, i) => (
                    <span
                      key={`${opt}-${i}`}
                      className="bg-zinc-800 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      {opt}
                      <button onClick={() => removeOption(i)} className="text-zinc-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {newField.allow_other && (
                    <span
                      className="bg-amber-500/20 border border-amber-500/40 text-amber-200 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      data-testid="new-field-other-pill"
                    >
                      Outro <span className="text-[10px] opacity-70">(abre campo de texto)</span>
                      <button
                        type="button"
                        onClick={() => setNewField({ ...newField, allow_other: false })}
                        className="text-amber-300/70 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
                {!newField.allow_other && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setNewField({ ...newField, allow_other: true })}
                    className="mt-2 border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                    data-testid="new-field-add-other"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Adicionar opção &quot;Outro&quot;
                  </Button>
                )}
                {newField.allow_other && (
                  <p className="text-zinc-500 text-xs mt-2">
                    Quando o comprador escolher &quot;Outro&quot;, aparece um campo de texto para ele escrever a sua resposta.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Campo Obrigatório</Label>
              <Switch
                checked={newField.required}
                onCheckedChange={(checked) => setNewField({ ...newField, required: checked })}
              />
            </div>

            <Button onClick={handleCreate} className="w-full btn-primary">
              Criar Campo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={!!editingField} onOpenChange={() => setEditingField(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Campo</DialogTitle>
          </DialogHeader>
          {editingField && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-300">Etiqueta</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  className="input-dark mt-2"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Nome Técnico</Label>
                <Input
                  value={editingField.name}
                  onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                  className="input-dark mt-2 font-mono"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Placeholder (texto dentro da caixa)</Label>
                <Input
                  value={editingField.placeholder || ""}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  placeholder="Ex: Escreva o seu nome..."
                  className="input-dark mt-2"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Tipo de Campo</Label>
                <Select
                  value={editingField.field_type}
                  onValueChange={(value) => setEditingField({ ...editingField, field_type: value, options: value === "dropdown" ? (editingField.options || []) : [] })}
                >
                  <SelectTrigger className="input-dark mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="text" className="text-white">Texto</SelectItem>
                    <SelectItem value="number" className="text-white">Número</SelectItem>
                    <SelectItem value="email" className="text-white">Email</SelectItem>
                    <SelectItem value="phone" className="text-white">Telefone</SelectItem>
                    <SelectItem value="date" className="text-white">Data de Nascimento</SelectItem>
                    <SelectItem value="dropdown" className="text-white">Lista de Opções</SelectItem>
                    <SelectItem value="checkbox" className="text-white">Checkbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingField.field_type === "dropdown" && (
                <div>
                  <Label className="text-zinc-300">Opções</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Adicionar opção"
                      className="input-dark"
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), (() => {
                        if (newOption.trim()) {
                          setEditingField({ ...editingField, options: [...(editingField.options || []), newOption.trim()] });
                          setNewOption("");
                        }
                      })())}
                    />
                    <Button onClick={() => {
                      if (newOption.trim()) {
                        setEditingField({ ...editingField, options: [...(editingField.options || []), newOption.trim()] });
                        setNewOption("");
                      }
                    }} type="button" className="btn-secondary">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(editingField.options || []).map((opt, i) => (
                      <span
                        key={`${opt}-${i}`}
                        className="bg-zinc-800 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {opt}
                        <button onClick={() => setEditingField({ ...editingField, options: editingField.options.filter((_, idx) => idx !== i) })} className="text-zinc-500 hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {editingField.allow_other && (
                      <span
                        className="bg-amber-500/20 border border-amber-500/40 text-amber-200 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        data-testid="edit-field-other-pill"
                      >
                        Outro <span className="text-[10px] opacity-70">(abre campo de texto)</span>
                        <button
                          type="button"
                          onClick={() => setEditingField({ ...editingField, allow_other: false })}
                          className="text-amber-300/70 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  {!editingField.allow_other && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingField({ ...editingField, allow_other: true })}
                      className="mt-2 border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                      data-testid="edit-field-add-other"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Adicionar opção &quot;Outro&quot;
                    </Button>
                  )}
                  {editingField.allow_other && (
                    <p className="text-zinc-500 text-xs mt-2">
                      Quando o comprador escolher &quot;Outro&quot;, aparece um campo de texto para ele escrever a sua resposta.
                    </p>
                  )}
                  {(!editingField.options || editingField.options.length === 0) && (
                    <p className="text-zinc-500 text-xs mt-2">Adicione pelo menos uma opção</p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Campo Obrigatório</Label>
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(checked) => setEditingField({ ...editingField, required: checked })}
                />
              </div>
              <Button onClick={handleUpdate} className="w-full btn-primary">
                Guardar Alterações
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
